import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useNavigate } from "@tanstack/react-router";
import { Check, MessageCircle, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useOnboarding } from "./OnboardingContext";
import { ONBOARDING_STEPS } from "./constants";

export function OnboardingStepper() {
	const { t } = useTranslation("onboarding");
	const { isActive, currentStep, dismiss, mode, chatThreadId } = useOnboarding();
	const navigate = useNavigate();

	if (!isActive) return null;

	const pct = Math.round((currentStep / ONBOARDING_STEPS.length) * 100);

	return (
		<div className="border-b">
			{/* Progress bar */}
			<div className="h-0.5 bg-muted">
				<div
					className="h-full bg-primary transition-all duration-500 ease-out"
					style={{ width: `${pct}%` }}
				/>
			</div>
			{/* Step labels */}
			<div className="flex items-center gap-0.5 px-4 py-1.5">
				<span className="text-xs font-medium text-muted-foreground mr-2">
					{t("getting_started")}
				</span>
				{mode === "chat" && (
					<span className="inline-flex items-center gap-1 text-[10px] text-primary bg-primary/10 rounded-full px-1.5 py-0.5 mr-1">
						<MessageCircle className="h-2.5 w-2.5" />
						{t("via_assistant")}
					</span>
				)}
				{ONBOARDING_STEPS.map((step, i) => {
					const done = i < currentStep;
					const active = i === currentStep;
					return (
						<Button
							key={step.key}
							variant="ghost"
							size="sm"
							className={cn(
								"gap-1 rounded-full px-2 py-0.5 h-auto text-xs",
								done && "text-primary hover:bg-primary/5",
								active && "font-medium text-foreground",
								!done && !active && "text-muted-foreground/50",
							)}
							onClick={() => {
								if (mode === "chat" && chatThreadId) {
									navigate({
										to: "/assistant/$threadId",
										params: { threadId: chatThreadId },
									});
									return;
								}
								navigate({ to: step.path });
							}}
						>
							<span
								className={cn(
									"flex h-4 w-4 items-center justify-center rounded-full text-[10px] shrink-0",
									done && "bg-primary text-primary-foreground",
									active && "border-2 border-primary text-primary font-bold",
									!done &&
										!active &&
										"border border-muted-foreground/25 text-muted-foreground/50",
								)}
							>
								{done ? <Check className="h-2.5 w-2.5" /> : i + 1}
							</span>
							<span className="hidden sm:inline">{t(step.labelKey)}</span>
							{/* Connector */}
							{i < ONBOARDING_STEPS.length - 1 && (
								<span
									className={cn(
										"ml-1 h-px w-4",
										i < currentStep ? "bg-primary/30" : "bg-border",
									)}
								/>
							)}
						</Button>
					);
				})}
				<div className="flex-1" />
				<Button
					variant="ghost"
					size="icon"
					className="h-auto w-auto p-1 text-muted-foreground/40 hover:text-muted-foreground"
					onClick={dismiss}
					title={t("skip_onboarding")}
					aria-label={t("skip_onboarding")}
				>
					<X className="h-3.5 w-3.5" />
				</Button>
			</div>
		</div>
	);
}
